import { useStore } from 'react-redux'
import { useExperiment as useSharedExperiment, useTriggerExperimentEvent as useSharedTriggerExperimentEvent } from '@afterpaytouch/feature'
import { activeExperimentsSelector } from './selectors'
import { useAppSelector } from '../hooks'
import { PortalExperimentName } from '../../providers/ExperimentProvider'
import { ExperimentValue } from './reducer'
import { ConsumerAppState } from '../store'

export interface Experiment {
  name: PortalExperimentName
  active: boolean
  loading: boolean
  control: boolean
  value: ExperimentValue
  assetId?: string | undefined
}

export const useActiveExperiments = (): string => {
  return useAppSelector((state) => {
    const activeExperiments = activeExperimentsSelector(state)
    return Object.entries(activeExperiments)
      .map(([experiment, value]) => `${experiment}${value}`)
      .join(',')
  })
}

export const useTriggerExperimentEvent = (): ((experiment: Experiment, customEventProperties?: Record<string, unknown>) => void) => {
  const { getState } = useStore<ConsumerAppState>()
  const sharedTrigger = useSharedTriggerExperimentEvent()

  return (experiment: Experiment, customEventProperties?: Record<string, unknown>): void => {
    // Check if experiment already exists in Redux store to prevent duplicate logging
    const activeExperiments = activeExperimentsSelector(getState())
    const experimentAlreadyFired = experiment.name in activeExperiments && activeExperiments[experiment.name] === experiment.value

    if (!experimentAlreadyFired) {
      // Call the shared trigger which will handle both analytics logging and Redux updates
      sharedTrigger(experiment, customEventProperties)
    }
  }
}

export function useExperiment(name: PortalExperimentName, fireEvent: boolean = true): Experiment {
  // Delegate to the shared implementation
  const sharedExperiment = useSharedExperiment(name, fireEvent)

  // Convert from shared package Experiment to our local Experiment type
  const experiment: Experiment = {
    name: sharedExperiment.name as PortalExperimentName,
    active: sharedExperiment.active,
    loading: sharedExperiment.loading,
    control: sharedExperiment.control,
    value: sharedExperiment.value as ExperimentValue,
    assetId: sharedExperiment.assetId,
  }

  return experiment
}
